import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { Terrain } from './Terrain';

export class CameraRig {
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  private terrain: Terrain;
  private readonly minClearance = 0.25; // metres above terrain

  // Fly-to tween state. When active, target + camera lerp toward destination
  // each update. Damping off during the tween so we don't fight ourselves.
  private flyT = 0;
  private flyDur = 0;
  private flyTargetStart = new THREE.Vector3();
  private flyTargetEnd = new THREE.Vector3();
  private flyCameraStart = new THREE.Vector3();
  private flyCameraEnd = new THREE.Vector3();

  // Follow mode — if set, camera continuously tracks this target each update.
  private followSource: (() => { x: number; y: number; z: number } | null) | null = null;

  // Ant-cam mode — first-person camera riding on the player ant.
  private antCamSource:
    | (() => { x: number; y: number; z: number; heading: number } | null)
    | null = null;
  private readonly defaultFov = 55;
  private readonly antCamFov = 85;

  constructor(dom: HTMLElement, terrain: Terrain) {
    this.terrain = terrain;
    const w = window.innerWidth;
    const h = window.innerHeight;
    // Far plane matched to the FogExp2 fade distance — beyond ~45 m the
    // fog has absorbed >90 % of light, so nothing past that is worth
    // drawing. Much cheaper than the old 500 m frustum.
    this.camera = new THREE.PerspectiveCamera(this.defaultFov, w / h, 0.05, 45);

    // Start looking at origin from a pleasant angle.
    const y0 = terrain.getHeight(0, 0);
    this.camera.position.set(16, y0 + 14, 22);

    this.controls = new OrbitControls(this.camera, dom);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.target.set(0, y0, 0);
    this.controls.minDistance = 1.2;
    this.controls.maxDistance = 120;
    this.controls.maxPolarAngle = Math.PI * 0.495; // never go below horizon
    this.controls.zoomSpeed = 0.9;
    this.controls.rotateSpeed = 0.7;
    this.controls.update();
  }

  resize = (w: number, h: number) => {
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  };

  /** Turn follow on/off. While on, target tracks the provided source each frame. */
  setFollow(source: (() => { x: number; y: number; z: number } | null) | null): void {
    this.followSource = source;
  }

  isFollowing(): boolean {
    return this.followSource !== null;
  }

  /** Turn first-person ant-cam on/off. While on, camera rides the ant's head. */
  setAntCam(
    source: (() => { x: number; y: number; z: number; heading: number } | null) | null,
  ): void {
    const was = this.antCamSource !== null;
    this.antCamSource = source;
    const now = this.antCamSource !== null;
    if (now && !was) {
      this.controls.enabled = false;
      this.flyDur = 0;
      this.camera.fov = this.antCamFov;
      this.camera.updateProjectionMatrix();
    } else if (!now && was) {
      this.controls.enabled = true;
      this.camera.fov = this.defaultFov;
      this.camera.updateProjectionMatrix();
    }
  }

  isAntCam(): boolean {
    return this.antCamSource !== null;
  }

  /** Abort any in-progress flyTo. Callers that snap the camera to a new
   *  position (e.g. NestMode.descend) need this so the lingering fly doesn't
   *  overwrite the position they just set on the next update tick. */
  cancelFly(): void {
    this.flyDur = 0;
  }

  /**
   * Smooth camera focus on a world point. By default preserves the user's
   * current angle/zoom. Pass a cameraOffset to override (useful for the intro
   * shot where we want to zoom all the way in on the ant).
   */
  flyTo(
    x: number,
    y: number,
    z: number,
    duration = 1.2,
    cameraOffset?: { x: number; y: number; z: number },
  ): void {
    if (this.antCamSource) return;
    const offset = cameraOffset
      ? new THREE.Vector3(cameraOffset.x, cameraOffset.y, cameraOffset.z)
      : this.camera.position.clone().sub(this.controls.target);
    this.flyTargetStart.copy(this.controls.target);
    this.flyTargetEnd.set(x, y, z);
    this.flyCameraStart.copy(this.camera.position);
    this.flyCameraEnd.copy(this.flyTargetEnd).add(offset);
    this.flyT = 0;
    this.flyDur = duration;
  }

  update(dt: number) {
    if (this.antCamSource) {
      const a = this.antCamSource();
      if (a) {
        const fx = Math.cos(a.heading);
        const fz = Math.sin(a.heading);
        const camX = a.x - fx * 0.02;
        const camZ = a.z - fz * 0.02;
        const camY = this.terrain.getHeight(camX, camZ) + 0.03;
        this.camera.position.set(camX, camY, camZ);
        const tx = a.x + fx * 0.5;
        const tz = a.z + fz * 0.5;
        const ty = this.terrain.getHeight(tx, tz);
        this.controls.target.set(tx, ty, tz);
      }
      this.controls.update();
      return;
    }
    if (this.flyDur > 0) {
      this.flyT = Math.min(1, this.flyT + dt / this.flyDur);
      const t = this.flyT;
      const e = t * t * (3 - 2 * t);
      this.controls.target.lerpVectors(this.flyTargetStart, this.flyTargetEnd, e);
      this.camera.position.lerpVectors(this.flyCameraStart, this.flyCameraEnd, e);
      if (this.flyT >= 1) this.flyDur = 0;
    } else if (this.followSource) {
      // Follow mode: keep the current camera-target offset, just translate the
      // whole frame so the ant stays dead-center. User can still orbit freely.
      const p = this.followSource();
      if (p) {
        const dx = p.x - this.controls.target.x;
        const dy = p.y - this.controls.target.y;
        const dz = p.z - this.controls.target.z;
        this.controls.target.set(p.x, p.y, p.z);
        this.camera.position.x += dx;
        this.camera.position.y += dy;
        this.camera.position.z += dz;
      }
    }
    this.controls.update();
    if (!this.allowBelowGround) {
      // Lift the orbit target to the terrain surface every frame so
      // orbiting/panning can never bury it underneath terrain. Without
      // this, a heightmap world (e.g. no-food, peaks ~5 m) lets the
      // user pan the target's XZ to a tall spot while target.y stays
      // at sea level — the camera sphere then dips below the surface
      // at certain pitches and the back-face-culled terrain reads as
      // transparent. Skip in follow / antCam modes (they own target.y).
      if (!this.followSource && !this.antCamSource && this.flyDur === 0) {
        const tg = this.controls.target;
        const groundAtTarget = this.terrain.getHeight(tg.x, tg.z);
        if (tg.y < groundAtTarget) tg.y = groundAtTarget;
      }
      // Lift the camera above the HIGHEST terrain along the camera->target
      // sight-line, not just the ground directly beneath it. At grazing
      // angles in a bumpy heightmap (no-food) a ridge between camera and
      // target — or the terrain edge when the orbit swings off the finite
      // world square — would otherwise poke through the near plane and the
      // back-face-culled terrain reads as transparent ("under the terrain").
      const p = this.camera.position;
      const tg = this.controls.target;
      let maxGround = this.sampleGround(p.x, p.z);
      const STEPS = 6;
      for (let i = 1; i <= STEPS; i++) {
        const t = i / STEPS;
        const gx = p.x + (tg.x - p.x) * t;
        const gz = p.z + (tg.z - p.z) * t;
        const g = this.sampleGround(gx, gz);
        if (g > maxGround) maxGround = g;
      }
      const ground = maxGround + this.minClearance;
      if (p.y < ground) p.y = ground;
    }
  }

  /** Terrain height with the sample point clamped into the world square.
   *  Outside the heightmap, terrain.getHeight returns 0 (sea level), which
   *  would let the camera dip below the raised terrain edge; clamping to the
   *  nearest in-bounds point yields the actual edge height instead. */
  private sampleGround(x: number, z: number): number {
    const half = this.terrain.opts.size * 0.5;
    const cx = Math.max(-half, Math.min(half, x));
    const cz = Math.max(-half, Math.min(half, z));
    return this.terrain.getHeight(cx, cz);
  }

  /** Surface mode clamps the camera above terrain; underground mode disables
   *  that clamp so the user can orbit below ground to inspect the nest. */
  private allowBelowGround = false;
  setAllowBelowGround(on: boolean): void { this.allowBelowGround = on; }
}
